import { useState, useEffect, useRef, useId } from "react";
import { apiGet } from "../lib/api.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskCreateFormProps {
	projectId: string;
	apiUrl: string;
	onSubmit: (message: string) => void;
	onClose: () => void;
}

interface AssigneeOption {
	id: string;
	name: string;
	/** Whether this is a team member or a client contact. */
	type: "member" | "contact";
}

/** Raw contact shape returned by GET /api/contacts/:projectId. */
interface ContactFromApi {
	id: string;
	name: string;
	email: string;
	company: string | null;
}

interface TaskListOption {
	id: string;
	name: string;
}

interface ValidationErrors {
	taskName?: string;
	dateRange?: string;
}

// ---------------------------------------------------------------------------
// Priority options (static)
// ---------------------------------------------------------------------------

const PRIORITY_OPTIONS = [
	{ value: "", label: "None" },
	{ value: "Low", label: "Low" },
	{ value: "Medium", label: "Medium" },
	{ value: "High", label: "High" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal form for creating a task via the /t slash command.
 * Does NOT call the API directly — assembles a structured message and
 * passes it to onSubmit, which routes through the normal AI confirmation pipeline.
 */
export default function TaskCreateForm({
	projectId,
	apiUrl: _apiUrl,
	onSubmit,
	onClose,
}: TaskCreateFormProps) {
	const headingId = useId();
	const nameId = useId();
	const descId = useId();
	const assigneeId = useId();
	const priorityId = useId();
	const startDateId = useId();
	const endDateId = useId();
	const taskListId = useId();

	// Ref for focus management and Escape key handling
	const modalRef = useRef<HTMLDivElement>(null);

	// Ref for the assignee dropdown wrapper — used for click-outside detection
	const assigneeWrapperRef = useRef<HTMLDivElement>(null);

	// Form field state
	const [taskName, setTaskName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedAssignees, setSelectedAssignees] = useState<AssigneeOption[]>([]);
	const [priority, setPriority] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [taskListId_val, setTaskListId] = useState("");

	// Data fetched from the API
	const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
	const [taskLists, setTaskLists] = useState<TaskListOption[]>([]);
	const [assigneesLoading, setAssigneesLoading] = useState(false);
	const [taskListsLoading, setTaskListsLoading] = useState(false);

	// Assignee dropdown state
	const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);

	// Validation
	const [submitted, setSubmitted] = useState(false);
	const [errors, setErrors] = useState<ValidationErrors>({});

	// -----------------------------------------------------------------------
	// Fetch users and task lists on mount
	// -----------------------------------------------------------------------

	useEffect(() => {
		let cancelled = false;

		setAssigneesLoading(true);
		(async () => {
			try {
				// Fetch team members and project contacts in parallel
				const [usersRes, contactsRes] = await Promise.all([
					apiGet(`/api/projects/${projectId}/users`),
					apiGet(`/api/contacts/${projectId}`),
				]);
				if (cancelled) return;

				const merged: AssigneeOption[] = [];

				// Team members
				if (usersRes.ok) {
					const usersData = (await usersRes.json()) as { users: Array<{ id: string; name: string }> };
					if (Array.isArray(usersData.users)) {
						for (const u of usersData.users) {
							merged.push({ id: u.id, name: u.name, type: "member" });
						}
					}
				}

				// Client contacts
				if (contactsRes.ok) {
					const contactsData = (await contactsRes.json()) as { contacts: ContactFromApi[] };
					if (Array.isArray(contactsData.contacts)) {
						for (const c of contactsData.contacts) {
							merged.push({
								id: c.id,
								name: c.name,
								type: "contact",
							});
						}
					}
				}

				setAssigneeOptions(merged);
			} catch {
				// Non-critical — assignee selection degrades gracefully
			} finally {
				if (!cancelled) setAssigneesLoading(false);
			}
		})();

		setTaskListsLoading(true);
		(async () => {
			try {
				const response = await apiGet(
					`/api/projects/${projectId}/task-lists`,
				);
				if (cancelled) return;
				if (response.ok) {
					const data = (await response.json()) as { taskLists: TaskListOption[] };
					if (Array.isArray(data.taskLists)) {
						setTaskLists(data.taskLists);
					}
				}
			} catch {
				// Non-critical — task list selection degrades gracefully
			} finally {
				if (!cancelled) setTaskListsLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [projectId]);

	// -----------------------------------------------------------------------
	// Focus management: move focus into modal on open, handle Escape to cancel
	// -----------------------------------------------------------------------

	useEffect(() => {
		// Move focus to the first focusable element (the task name input) on mount.
		// The autoFocus attribute on the name input handles this, but we also
		// need to intercept Escape key presses to cancel the modal.
		const modal = modalRef.current;
		if (!modal) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				// If the assignee dropdown is open, close it first.
				// A second Escape then closes the modal.
				if (assigneeDropdownOpen) {
					setAssigneeDropdownOpen(false);
				} else {
					onClose();
				}
			}
		};

		modal.addEventListener("keydown", handleKeyDown);
		return () => modal.removeEventListener("keydown", handleKeyDown);
	}, [onClose, assigneeDropdownOpen]);

	// -----------------------------------------------------------------------
	// Close assignee dropdown when clicking outside the wrapper
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (!assigneeDropdownOpen) return;

		const handleMouseDown = (e: MouseEvent) => {
			if (
				assigneeWrapperRef.current &&
				!assigneeWrapperRef.current.contains(e.target as Node)
			) {
				setAssigneeDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleMouseDown);
		return () => document.removeEventListener("mousedown", handleMouseDown);
	}, [assigneeDropdownOpen]);

	// -----------------------------------------------------------------------
	// Validation
	// -----------------------------------------------------------------------

	function validate(): ValidationErrors {
		const errs: ValidationErrors = {};

		if (!taskName.trim()) {
			errs.taskName = "Task name is required.";
		}

		if (startDate && endDate && endDate < startDate) {
			errs.dateRange = "End date must be on or after the start date.";
		}

		return errs;
	}

	// -----------------------------------------------------------------------
	// Assignee toggle
	// -----------------------------------------------------------------------

	function toggleAssignee(assignee: AssigneeOption) {
		setSelectedAssignees((prev) => {
			const exists = prev.some((a) => a.id === assignee.id);
			if (exists) {
				return prev.filter((a) => a.id !== assignee.id);
			}
			return [...prev, assignee];
		});
	}

	function removeAssignee(userId: string) {
		setSelectedAssignees((prev) => prev.filter((u) => u.id !== userId));
	}

	// -----------------------------------------------------------------------
	// Submit handler — assembles a structured chat message
	// -----------------------------------------------------------------------

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitted(true);

		const errs = validate();
		setErrors(errs);

		if (Object.keys(errs).length > 0) {
			return;
		}

		// Assemble the message parts
		const parts: string[] = [`Create task: ${taskName.trim()}`];

		if (description.trim()) {
			parts.push(`description "${description.trim()}"`);
		}

		if (selectedAssignees.length > 0) {
			const assigneeTokens = selectedAssignees
				.map((a) =>
					a.type === "member"
						? `[@${a.name}:user_${a.id}]`
						: a.name,
				)
				.join(", ");
			parts.push(`assigned to ${assigneeTokens}`);
		}

		if (priority) {
			parts.push(`priority ${priority}`);
		}

		if (startDate) {
			parts.push(`start date ${startDate}`);
		}

		if (endDate) {
			parts.push(`end date ${endDate}`);
		}

		if (taskListId_val) {
			const list = taskLists.find((tl) => tl.id === taskListId_val);
			if (list) {
				parts.push(`in task list "${list.name}"`);
			}
		}

		const message = parts.join(", ");
		onSubmit(message);
		onClose();
	}

	// -----------------------------------------------------------------------
	// Derived validation state — only surfaced after first submit attempt
	// -----------------------------------------------------------------------

	const fieldError = (field: keyof ValidationErrors) =>
		submitted ? errors[field] : undefined;

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="task-create-form-overlay" role="presentation">
			<div
				ref={modalRef}
				className="task-create-form-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby={headingId}
			>
				{/* ----- Heading ----- */}
				<div className="task-create-form__header">
					<h2 id={headingId} className="task-create-form__heading">
						<span className="task-create-form__heading-icon" aria-hidden="true">
							{/* Checkbox icon */}
							<svg width="18" height="18" viewBox="0 0 16 16" fill="none">
								<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
								<path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
							</svg>
						</span>
						Create Task
					</h2>
					<button
						type="button"
						className="task-create-form__close"
						onClick={onClose}
						aria-label="Close"
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
							<path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
						</svg>
					</button>
				</div>

				<div className="task-create-form__hint">
					This will generate a task creation request for AI confirmation.
				</div>

				<form
					className="task-create-form__form"
					onSubmit={handleSubmit}
					noValidate
				>
					{/* Task Name (required) */}
					<div className="task-create-form__field">
						<label htmlFor={nameId} className="task-create-form__label">
							Task Name <span className="task-create-form__required" aria-hidden="true">*</span>
						</label>
						<input
							id={nameId}
							type="text"
							className={
								fieldError("taskName")
									? "task-create-form__input task-create-form__input--error"
									: "task-create-form__input"
							}
							value={taskName}
							onChange={(e) => setTaskName(e.target.value)}
							maxLength={200}
							required
							aria-required="true"
							aria-invalid={fieldError("taskName") ? "true" : undefined}
							aria-describedby={fieldError("taskName") ? `${nameId}-error` : undefined}
							placeholder="What needs to be done?"
							autoFocus
						/>
						<div className="task-create-form__char-count">
							{taskName.length}/200
						</div>
						{fieldError("taskName") && (
							<span
								id={`${nameId}-error`}
								className="task-create-form__field-error"
								role="alert"
							>
								{fieldError("taskName")}
							</span>
						)}
					</div>

					{/* Description (optional) */}
					<div className="task-create-form__field">
						<label htmlFor={descId} className="task-create-form__label">
							Description
						</label>
						<textarea
							id={descId}
							className="task-create-form__textarea"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							maxLength={10000}
							rows={3}
							placeholder="Add more detail (optional)…"
						/>
						<div className="task-create-form__char-count">
							{description.length}/10,000
						</div>
					</div>

					{/* Assignees (optional, multi-select) */}
					<div className="task-create-form__field">
						<label htmlFor={assigneeId} className="task-create-form__label">
							Assignee(s)
						</label>

						{/* Selected assignee chips */}
						{selectedAssignees.length > 0 && (
							<div className="task-create-form__assignee-chips">
								{selectedAssignees.map((a) => (
									<span
										key={a.id}
										className={`task-create-form__assignee-chip${a.type === "contact" ? " task-create-form__assignee-chip--contact" : ""}`}
									>
										<svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
											<circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
											<path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
										</svg>
										{a.name}
										{a.type === "contact" && (
											<span className="task-create-form__client-badge">Client</span>
										)}
										<button
											type="button"
											className="task-create-form__assignee-chip-remove"
											onClick={() => removeAssignee(a.id)}
											aria-label={`Remove ${a.name}`}
										>
											&times;
										</button>
									</span>
								))}
							</div>
						)}

						{/* Dropdown trigger */}
						<div className="task-create-form__assignee-wrapper" ref={assigneeWrapperRef}>
							<button
								id={assigneeId}
								type="button"
								className="task-create-form__select task-create-form__assignee-trigger"
								onClick={() => setAssigneeDropdownOpen((o) => !o)}
								aria-expanded={assigneeDropdownOpen}
								aria-haspopup="listbox"
							>
								<span>{assigneesLoading ? "Loading team members…" : "Select assignee(s)…"}</span>
								<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={`task-create-form__select-chevron${assigneeDropdownOpen ? " task-create-form__select-chevron--open" : ""}`}>
									<path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
								</svg>
							</button>

							{assigneeDropdownOpen && assigneeOptions.length > 0 && (() => {
								const members = assigneeOptions.filter((a) => a.type === "member");
								const contacts = assigneeOptions.filter((a) => a.type === "contact");
								const hasGroups = members.length > 0 && contacts.length > 0;
								return (
									<ul
										className="task-create-form__assignee-dropdown"
										role="listbox"
										aria-multiselectable="true"
										aria-label="Select assignees"
									>
										{hasGroups && (
											<li className="task-create-form__assignee-group-label" aria-hidden="true">
												Team Members
											</li>
										)}
										{members.map((a) => {
											const isSelected = selectedAssignees.some((s) => s.id === a.id);
											return (
												<li
													key={a.id}
													className={`task-create-form__assignee-option${isSelected ? " task-create-form__assignee-option--selected" : ""}`}
													role="option"
													aria-selected={isSelected}
													onMouseDown={(e) => {
														e.preventDefault();
														toggleAssignee(a);
													}}
												>
													{isSelected && (
														<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
															<path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
														</svg>
													)}
													{a.name}
												</li>
											);
										})}
										{contacts.length > 0 && (
											<li className="task-create-form__assignee-group-label" aria-hidden="true">
												Client Contacts
											</li>
										)}
										{contacts.map((a) => {
											const isSelected = selectedAssignees.some((s) => s.id === a.id);
											return (
												<li
													key={a.id}
													className={`task-create-form__assignee-option${isSelected ? " task-create-form__assignee-option--selected" : ""}`}
													role="option"
													aria-selected={isSelected}
													onMouseDown={(e) => {
														e.preventDefault();
														toggleAssignee(a);
													}}
												>
													{isSelected && (
														<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
															<path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
														</svg>
													)}
													{a.name}
													<span className="task-create-form__client-badge">Client</span>
												</li>
											);
										})}
									</ul>
								);
							})()}

							{assigneeDropdownOpen && !assigneesLoading && assigneeOptions.length === 0 && (
								<div className="task-create-form__assignee-empty">
									No team members found. Use /u in the chat to search for assignees.
								</div>
							)}
						</div>
					</div>

					{/* Priority (optional, single-select) */}
					<div className="task-create-form__field">
						<label htmlFor={priorityId} className="task-create-form__label">
							Priority
						</label>
						<select
							id={priorityId}
							className="task-create-form__select"
							value={priority}
							onChange={(e) => setPriority(e.target.value)}
						>
							{PRIORITY_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					{/* Date row — Start and End side by side */}
					<div className="task-create-form__date-row">
						<div className="task-create-form__field">
							<label htmlFor={startDateId} className="task-create-form__label">
								Start Date
							</label>
							<input
								id={startDateId}
								type="date"
								className="task-create-form__input task-create-form__input--date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>

						<div className="task-create-form__field">
							<label htmlFor={endDateId} className="task-create-form__label">
								End Date
							</label>
							<input
								id={endDateId}
								type="date"
								className={
									fieldError("dateRange")
										? "task-create-form__input task-create-form__input--date task-create-form__input--error"
										: "task-create-form__input task-create-form__input--date"
								}
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								aria-invalid={fieldError("dateRange") ? "true" : undefined}
							/>
						</div>
					</div>

					{fieldError("dateRange") && (
						<span className="task-create-form__field-error" role="alert">
							{fieldError("dateRange")}
						</span>
					)}

					{/* Task List (optional, single-select) */}
					<div className="task-create-form__field">
						<label htmlFor={taskListId} className="task-create-form__label">
							Task List
						</label>
						{taskListsLoading ? (
							<div className="task-create-form__loading-text">Loading task lists…</div>
						) : (
							<select
								id={taskListId}
								className="task-create-form__select"
								value={taskListId_val}
								onChange={(e) => setTaskListId(e.target.value)}
							>
								<option value="">No specific list</option>
								{taskLists.map((tl) => (
									<option key={tl.id} value={tl.id}>
										{tl.name}
									</option>
								))}
							</select>
						)}
					</div>

					{/* Actions */}
					<div className="task-create-form__actions">
						<button
							type="button"
							className="task-create-form__btn task-create-form__btn--cancel"
							onClick={onClose}
							aria-label="Cancel task creation"
						>
							Cancel
						</button>
						<button
							type="submit"
							className="task-create-form__btn task-create-form__btn--submit"
						>
							<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
								<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
								<path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
							</svg>
							Create Task
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
